import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import actions from './option_groups.actions';
import optionGroupsAdapter from './option_groups.adapter';
import utils from './option_groups.utils';

import type { LoadingStatus } from 'types/common_types';

type OptionGroupsSliceInitialState = {
  status: LoadingStatus;
  appliedFilters: Record<string, string>;
  /** Some products have no options, but we need to know them explicitly */
  emptyTemplateIds: number[];
};

const initialState: OptionGroupsSliceInitialState = {
  status: 'idle',
  appliedFilters: {},
  emptyTemplateIds: [],
};

const slice = createSlice({
  name: 'optionGroups',
  initialState: optionGroupsAdapter.getInitialState(initialState),
  reducers: {
    addMany: optionGroupsAdapter.addMany,
    reset() {
      return optionGroupsAdapter.getInitialState(initialState);
    },
    setAppliedOptionGroupFilters(state, { payload }: PayloadAction<Record<string, string>>) {
      return { ...state, appliedFilters: { ...state.appliedFilters, ...payload } };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(actions.fetchOptionGroups.pending, (state) => {
        return { ...state, status: 'loading' };
      })
      .addCase(actions.fetchOptionGroups.fulfilled, (state, { payload }) => {
        if (payload) {
          const { optionGroups, templateId } = payload;

          const update = optionGroupsAdapter.addMany(
            state,
            optionGroups.map((og) => utils.formatOptionGroup(og, templateId))
          );

          if (optionGroups.length === 0) {
            update.emptyTemplateIds.push(templateId);
          }

          update.status = 'idle';
          return update;
        } else {
          return { ...state, status: 'idle' };
        }
      })
      .addCase(actions.fetchOptionGroups.rejected, (state) => {
        // Handle an error, send more details to sentry, etc
        return { ...state, status: 'error' };
      })
      .addCase(actions.fetchOrderOptions.fulfilled, (state, { payload }) => {
        const update = optionGroupsAdapter.addMany(
          state,
          payload.map((og) => utils.formatOptionGroup(og, 'order-options'))
        );
        update.status = 'idle';
        return update;
      });
  },
});

export default slice;
